/**
 * Guided creation flow: a social media post (Facebook, saved as a draft).
 *
 * Hangs off the `social-media` module stop — that module has no expandable
 * sidebar group (see `tours.ts`'s comment on modules with "nothing to
 * expand"), so unlike the automations/campaigns flows this one has no
 * submodule id to attach to; the bare module step is the only option.
 *
 * ┌─ WHY FACEBOOK, AND WHY A DRAFT, NOT A REAL PUBLISH ────────────────────┐
 * │ The composer opens on whichever platform tab is active. Instagram never│
 * │ allows a caption-only post — every format (Feed/Reel/Story/Carousel)   │
 * │ requires at least one photo/video (`InstagramCreatePostDialog.tsx`     │
 * │ filters `'text'`/`'link'` out of its own format list). Facebook's own   │
 * │ "Text" format is the one caption-only option available anywhere here.  │
 * │                                                                        │
 * │ 🔴 THE COMPOSER DOES NOT OPEN ON "Text" BY ITSELF — an earlier version │
 * │ of this file assumed it did, because `FacebookCreatePostDialog`'s own   │
 * │ prop default is `initialPostType = 'text'`. But `SocialMediaPostsManager│
 * │ .tsx` always passes an EXPLICIT `initialPostType={globalCreatePostType}`,│
 * │ which defaults to `'feed'` — and `postTypeToFacebookMediaType('feed')`  │
 * │ (`createPostTypes.ts`) falls through to `'PHOTO'`. So "Create post"     │
 * │ opens the Facebook composer in PHOTO mode by default. `select-format`   │
 * │ below clicks the "Text" chip explicitly rather than trusting that      │
 * │ default — do not remove it on the assumption the default is already    │
 * │ right.                                                                  │
 * │                                                                        │
 * │ The final action is Save Draft, never Publish/Schedule. Publishing a   │
 * │ WhatsApp template only ever produces a PENDING row in this app's own   │
 * │ database (see `MESSAGE_TEMPLATE_TASK`) — but "Publish now" here calls   │
 * │ the real Meta Graph API and posts live content to whatever Facebook    │
 * │ Page is connected. A guided walkthrough must never be the thing that   │
 * │ puts placeholder copy on a real, external, customer-facing account.    │
 * │ `saveDraft()` (`FacebookCreatePostDialog.tsx`) writes `status: 'draft'` │
 * │ to this app's own backend and touches Meta's API not at all.           │
 * └────────────────────────────────────────────────────────────────────────┘
 */

import { sidebarAnchor, type AnchorSpec } from '../engine/anchors';
import { clickWhenReady, delay, findVisible, isFilled, waitUntil } from './formFill';
import { closeFlowChrome } from './closeChrome';
import type { TaskFlow } from './types';

/** The Social Media Posts workspace. */
const SOCIAL_MEDIA_PATH = '/home/social-media/posts';

/** The sidebar's own "Social Media" row — a plain link, no accordion to open first. */
const SIDEBAR_LINK = sidebarAnchor('Social Media', 'nav-social-media');

/** Every platform tab lives inside this row (`SocialMediaPostsManager.tsx`). */
const NETWORK_TABS_SELECTOR = '[data-testid="social-posts-network-tabs"]';

/**
 * The list page's "Create post" button.
 *
 * There are genuinely two of these in the DOM depending on whether the list
 * is empty (a toolbar button and a separate empty-state button) — matched by
 * label alone, same reasoning as `AUTOMATION_FLOW_TASK`'s `CREATE_AUTOMATION_BUTTON`:
 * only one is ever visible at a time, and `resolveAnchor` already skips
 * invisible candidates.
 */
const CREATE_POST_BUTTON: AnchorSpec = {
  structural: { within: 'body', match: 'button', labels: ['Create post'] },
};

/**
 * The "Text" format chip — scoped to the open dialog because "Text" alone is
 * too generic a label to trust globally (unlike "Create post"/"Save draft",
 * which are distinctive enough that a body-wide match is safe).
 */
const TEXT_FORMAT_CHIP: AnchorSpec = {
  structural: { within: '[role="dialog"]', match: 'button', labels: ['Text'] },
};

/** The composer's caption field — a plain, id-less `<textarea>` inside its Sheet. */
const CAPTION_SELECTOR = '[role="dialog"] textarea';

/** Facebook-only: appears when the composer was opened with an `onSaveDraft` handler. */
const SAVE_DRAFT_BUTTON: AnchorSpec = { selector: '[data-testid="create-post-save-draft"]' };

const DUMMY_CAPTION =
  'Excited to share our latest update! 🎉 Take a look and let us know what you think in the comments.';

/**
 * Longest we will wait for the workspace's OWN initial load to finish before
 * giving up and proceeding anyway.
 *
 * 🔴 THIS GUARDS A RACE CONDITION IN THE HOST PAGE, not just pacing.
 * `SocialMediaPostsManager.tsx` loads every platform's posts once, right after
 * the page mounts (its accounts-loading effect, gated on `accountsLoading` /
 * `activeAccountsReady`), and that fetch has no generation guard: whichever of
 * two overlapping requests for the same data resolves LAST simply overwrites
 * the other's result in state, no matter which one started first.
 *
 * A human takes many seconds to read the page, open the composer, and write a
 * caption before saving — the page's own initial load has always long
 * finished by then, so there is nothing left in flight to race against. This
 * flow does the same in well under a second per step: it can create-and-save
 * a draft, get its OWN post-save refresh back first, and then have the
 * page's slower initial load (kicked off moments earlier, but still in
 * flight) land AFTER it and silently erase the just-created draft from the
 * list — the exact "saved, but doesn't show up" symptom this guard exists to
 * close off. Confirmed to reproduce only through this automated flow, never
 * on a manual save, which is the signature of this kind of race.
 *
 * This can only be MITIGATED from here, not fixed here: the race lives in the
 * host page's own fetch, which this module does not (and must not) edit.
 * Waiting for that load to visibly finish before creating anything closes the
 * window instead of guessing at a fixed pause — see `initialLoadSettled`.
 */
const INITIAL_LOAD_TIMEOUT_MS = 10000;

/** Are we on the list page, with its network tab bar rendered? */
const onSocialMediaListPage = () =>
  window.location.pathname === SOCIAL_MEDIA_PATH && Boolean(document.querySelector(NETWORK_TABS_SELECTOR));

/**
 * Has the workspace's own initial load actually finished, rather than merely
 * "some time has passed"?
 *
 * The page renders shimmering `animate-pulse` skeleton placeholders (see
 * `Skeleton` in `components/ui/skeleton.tsx`) for both the network tab bar and
 * the post grid for as long as its own account/post fetches are in flight —
 * they disappear the instant `setAccountsLoading`/`setScheduledLoading`
 * actually run. Polling for their absence is a real signal tied to the fetch
 * this flow needs to have finished before it creates anything (see
 * `INITIAL_LOAD_TIMEOUT_MS`), rather than a fixed timeout that is either too
 * short on a slow connection or wastefully long on a fast one.
 */
const initialLoadSettled = () => document.querySelectorAll('.animate-pulse').length === 0;

/**
 * A network tab button, found by the START of its own label — not an exact
 * match. Each button's accessible text is the platform name immediately
 * followed by an unread-count badge with no separating space
 * (`{t.label}<span>{t.count}</span>` in `SocialMediaPostsManager.tsx` renders
 * as e.g. `"Facebook12"`), so an exact `labels` match in the anchor system
 * would never resolve. Written as a plain DOM query instead of an `AnchorSpec`
 * for exactly that reason.
 */
function findNetworkTabButton(labelPrefix: string): HTMLElement | null {
  const container = document.querySelector(NETWORK_TABS_SELECTOR);
  if (!container) return null;
  const want = labelPrefix.trim().toLowerCase();
  const buttons = Array.from(container.querySelectorAll('button')) as HTMLElement[];
  return buttons.find((button) => (button.textContent || '').trim().toLowerCase().startsWith(want)) ?? null;
}

/** Is the Facebook tab the active one? Marked with `data-network-tab-active="true"`. */
function isFacebookTabActive(): boolean {
  return findNetworkTabButton('facebook')?.getAttribute('data-network-tab-active') === 'true';
}

/** Is the composer's own caption field on screen? */
const composerOpen = () => Boolean(findVisible(CAPTION_SELECTOR));

/** Is the "Text" chip the selected format? `FormatChips.tsx` marks it `aria-pressed`. */
function isTextFormatActive(): boolean {
  const dialog = document.querySelector('[role="dialog"]');
  if (!dialog) return false;
  const buttons = Array.from(dialog.querySelectorAll('button')) as HTMLElement[];
  const chip = buttons.find((button) => (button.textContent || '').trim().toLowerCase() === 'text');
  return chip?.getAttribute('aria-pressed') === 'true';
}

export const SOCIAL_MEDIA_POST_TASK: TaskFlow = {
  id: 'create-social-media-post',
  stepId: 'social-media',
  cta: 'Try it: create a social media post',
  preparingLabel: 'Opening Social Media…',
  title: 'Create a social media post',
  summary: 'Switch to Facebook, pick Text, write a caption, and save it as a draft — you press the buttons.',
  doneTitle: 'Post drafted',
  doneDescription:
    'Saved to your Facebook tab as a draft — nothing was posted. Reopen it any time from the Draft filter to add media, then Publish or Schedule when you are ready.',

  /**
   * Get to the workspace BEFORE the flow's own chrome appears — same
   * reasoning as `MESSAGE_TEMPLATE_TASK.beforeStart`. No accordion to open
   * first: "Social Media" is a plain sidebar link.
   */
  beforeStart: async () => {
    if (!onSocialMediaListPage()) {
      await clickWhenReady(SIDEBAR_LINK);
      const arrived = await waitUntil(onSocialMediaListPage, 5000);
      if (!arrived) {
        console.warn(
          '[tour-guide] "create-social-media-post": never confirmed arrival on the social media ' +
            'workspace. The next step may resolve the wrong button.',
        );
      }
    }
    // See `INITIAL_LOAD_TIMEOUT_MS` — let the workspace's own initial fetch of
    // every platform's posts actually finish before this flow creates
    // anything, so that fetch can never resolve LATER than (and overwrite)
    // the flow's own post-save refresh.
    const settled = await waitUntil(initialLoadSettled, INITIAL_LOAD_TIMEOUT_MS);
    if (!settled) {
      console.warn(
        '[tour-guide] "create-social-media-post": the workspace was still showing loading ' +
          `placeholders after ${INITIAL_LOAD_TIMEOUT_MS}ms — proceeding anyway, but a draft ` +
          'created from here may race the page\'s own slow initial load.',
      );
    }
  },

  /**
   * Close the post composer this flow opened — see `TaskFlow.afterEnd` and
   * `closeChrome.ts`.
   *
   * 🔴 WITHOUT IT, "SKIP MODULE" LEFT THE COMPOSER ON SCREEN and the tour resumed
   * under its backdrop: the next module's highlight was drawn beneath a Radix
   * modal, which is both halves of the report — the module not shown properly, and
   * a page that never went back to normal.
   *
   * The composer creates nothing until Save draft / Publish, so closing it discards
   * only the caption the guide typed. A run that finished has already saved and
   * closed it, and finds nothing to do here.
   */
  afterEnd: async () => {
    await closeFlowChrome(composerOpen);
  },

  steps: [
    {
      id: 'switch-to-facebook',
      label: 'Facebook tab',
      title: 'Switch to Facebook',
      description:
        'This is every post across your connected networks. Switch to the Facebook tab first — its composer can publish a plain text update with no photo or video required, which is the quickest way to see how this works.',
      anchor: { selector: NETWORK_TABS_SELECTOR },
      side: 'bottom',
      spotlight: true,
      ready: onSocialMediaListPage,
      prepare: async () => {},
      onNext: async () => {
        if (isFacebookTabActive()) return;
        findNetworkTabButton('facebook')?.click();
        await waitUntil(isFacebookTabActive, 1000);
        // The workspace's own initial load was already waited out in
        // `beforeStart` (see `initialLoadSettled`) — this is just a small
        // beat for React to finish committing the tab switch itself before
        // the next step goes looking for "Create post" on it.
        await delay(200);
      },
      satisfied: isFacebookTabActive,
      waitingHint: 'Press Next to switch to the Facebook tab.',
      // A workspace with no Facebook account connected has no such tab at
      // all — nothing to switch to, and no reason to stall on it.
      optional: true,
    },

    {
      id: 'open-composer',
      label: 'New post',
      title: 'Start a new post',
      description:
        'Create post opens the composer for whichever Facebook Page you have connected. Press Next to open it.',
      anchor: CREATE_POST_BUTTON,
      side: 'bottom',
      spotlight: true,
      // 🔴 GATED ON THE FACEBOOK TAB, NOT JUST THE ROUTE. If the previous
      // step's tab switch failed (no Facebook account connected, or the tab
      // simply is not there), "Create post" would still exist — but on
      // whatever tab is actually active it opens a DIFFERENT dialog
      // (`GlobalCreatePostDialog` on "All networks"), which has no Save Draft
      // button at all. Without this gate the flow would silently drive the
      // wrong composer for the rest of its steps and end with nothing
      // created and no clear reason why.
      ready: () => onSocialMediaListPage() && isFacebookTabActive(),
      prepare: async () => {},
      onNext: async () => {
        if (composerOpen()) return;
        await clickWhenReady(CREATE_POST_BUTTON);
      },
      satisfied: composerOpen,
      /**
       * Back over this step closes what its Next opened — see `TaskStep.rewind`.
       *
       * Without it, Back from the step after this one landed HERE with the
       * post composer still covering the Create post
       * button this step is about, which `resolve()` then refuses to ring: a step on screen with nothing
       * highlighted, which is what "Back disturbs the tour" looks like.
       */
      rewind: async () => {
        await closeFlowChrome(composerOpen, 'rewind');
      },
      waitingHint: 'Press Next to open the composer.',
    },

    /**
     * The composer does NOT open on "Text" by itself — see the file's own
     * doc comment. Selecting it explicitly is what actually makes this flow
     * caption-only; skipping this step would leave the post in Photo mode.
     */
    {
      id: 'select-format',
      label: 'Text format',
      title: 'Pick the Text format',
      description:
        'Click "Text". Facebook posts can be a photo, a video, a link, or plain text — Text is the only one of those that needs no media attached.',
      anchor: TEXT_FORMAT_CHIP,
      side: 'left',
      spotlight: true,
      ready: composerOpen,
      prepare: async () => {},
      onNext: async () => {
        if (isTextFormatActive()) return;
        await clickWhenReady(TEXT_FORMAT_CHIP);
      },
      satisfied: isTextFormatActive,
      waitingHint: 'Press Next to switch to the Text format.',
      // Some accounts/plans may not offer every format chip — if "Text" is
      // ever unavailable, there is nothing to switch to and no reason to stall.
      optional: true,
    },

    {
      id: 'write-caption',
      label: 'Caption',
      title: 'Write the caption',
      description:
        'Filled in for you — edit it if you like. This is the text that goes out with the post; Facebook is the one network here that does not need a photo or video alongside it.',
      anchor: { selector: CAPTION_SELECTOR },
      side: 'left',
      spotlight: true,
      ready: composerOpen,
      fill: [[CAPTION_SELECTOR, DUMMY_CAPTION]],
      satisfied: () => isFilled(CAPTION_SELECTOR),
      waitingHint: 'Type a caption to carry on.',
    },

    {
      id: 'save-draft',
      label: 'Save draft',
      title: 'Save it as a draft',
      description:
        'Click Save Draft. Nothing is posted anywhere — it lands under the Draft filter exactly like a real draft, ready to finish (add media, pick a time) whenever you want to actually publish it.',
      anchor: SAVE_DRAFT_BUTTON,
      side: 'top',
      spotlight: true,
      ready: composerOpen,
      prepare: async () => {},
      // 🔴 MUST WAIT FOR THE COMPOSER TO ACTUALLY CLOSE, NOT JUST FOR THE CLICK
      // TO BE DISPATCHED. `clickWhenReady` resolves the instant `.click()` is
      // called — it does not wait for whatever that click triggers to finish.
      // `saveDraft` (`FacebookCreatePostDialog.tsx`) is `async`: it awaits the
      // `/facebook/posts/schedule` network call before calling `onOpenChange(false)`,
      // which is what actually closes the composer. Without waiting here,
      // `advanceFromNext` (`TaskEngine.ts`) checks `satisfied()` — `!composerOpen()`
      // — the instant `onNext` resolves, while the save is still in flight and the
      // composer is still open. That always recorded this step as "skipped", which
      // is why the flow never showed its own success step after a real, successful
      // save: it finished having (wrongly) logged the one step that matters as
      // skipped. `waitUntil` polls until the composer actually closes (or a
      // generous timeout elapses for a slow/failed save), so `satisfied()` sees the
      // true outcome.
      onNext: async () => {
        await clickWhenReady(SAVE_DRAFT_BUTTON);
        await waitUntil(() => !composerOpen(), 8000);
      },
      // The composer closes itself on a successful save (`onOpenChange(false)`
      // in `FacebookCreatePostDialog.tsx`'s `saveDraft`). If it stays open —
      // no Facebook Page connected, or a real API failure — the honest
      // outcome is waiting, same as `MESSAGE_TEMPLATE_TASK`'s `submit` step.
      satisfied: () => !composerOpen(),
      waitingHint: 'Press Next to save this as a draft.',
    },
  ],
};
